import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FiArrowLeft, FiCalendar, FiClock,
  FiAlertCircle, FiCheck, FiX, FiRefreshCw,
  FiTrendingDown, FiChevronLeft, FiChevronRight, FiList,
  FiPlus, FiAward, FiFileText
} from 'react-icons/fi';
import ManagerAddLeaveModal from './ManagerAddLeaveModal';
import CreditEarnedLeaveModal from './CreditEarnedLeaveModal';
import { fmtDate, getCurrentFinancialYear } from '../../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('authToken');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const initials = (name = '') =>
  name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '??';

const toDateStr = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val.split('T')[0];
  if (val instanceof Date) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (Array.isArray(val)) {
    const [y, m, d] = val;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
};

export default function EmployeeLeaveSummaryPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const empFromState = location.state?.emp || null;

  const [balanceData, setBalanceData] = useState(null);
  const [allLeaves, setAllLeaves] = useState([]);
  const [earnedLeaves, setEarnedLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Active view tab: 'calendar' | 'applications' | 'earned'
  const [activeTab, setActiveTab] = useState('calendar');
  const [appFilter, setAppFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'

  const [calDate, setCalDate] = useState(new Date());
  const [scheduleView, setScheduleView] = useState('calendar');
  const [addLeaveOpen, setAddLeaveOpen] = useState(false);
  const [creditEarnedOpen, setCreditEarnedOpen] = useState(false);

  const year = getCurrentFinancialYear();
  const empName = empFromState?.fullName || empFromState?.name || `Employee ${employeeId}`;
  const empJobTitle = empFromState?.jobTitle || '';
  const empDept = empFromState?.department || '';

  const fetchData = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    setError('');
    Promise.allSettled([
      api.get(`/leave/subordinate/${employeeId}/monthly-balance/${year}`),
      api.get(`/leave/subordinate/${employeeId}`),
      api.get(`/leave/subordinate/${employeeId}/earned-leaves`),
      api.get('/holidays'),
    ]).then(([balRes, lvRes, elRes, holRes]) => {
      if (balRes.status === 'fulfilled') {
        setBalanceData(balRes.value.data?.data ?? balRes.value.data);
      } else {
        setError('Could not load leave balance.');
      }
      if (lvRes.status === 'fulfilled') {
        const raw = lvRes.value.data?.data ?? lvRes.value.data ?? [];
        setAllLeaves(Array.isArray(raw) ? raw : []);
      }
      if (elRes.status === 'fulfilled') {
        const raw = elRes.value.data?.data ?? elRes.value.data ?? [];
        setEarnedLeaves(Array.isArray(raw) ? raw : []);
      }
      if (holRes.status === 'fulfilled') {
        const raw = holRes.value.data;
        setHolidays(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
      }
    }).catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [employeeId, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pendingList = useMemo(() => allLeaves.filter(l => l.status === 'PENDING'), [allLeaves]);
  const approvedList = useMemo(() => allLeaves.filter(l => l.status === 'APPROVED'), [allLeaves]);
  const rejectedList = useMemo(() => allLeaves.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED'), [allLeaves]);

  const filteredApplications = useMemo(() => {
    if (appFilter === 'PENDING') return pendingList;
    if (appFilter === 'APPROVED') return approvedList;
    if (appFilter === 'REJECTED') return rejectedList;
    return allLeaves;
  }, [appFilter, allLeaves, pendingList, approvedList, rejectedList]);

  const changeMonth = (off) => setCalDate(p => {
    const n = new Date(p);
    n.setMonth(n.getMonth() + off);
    return n;
  });

  const getLeaveForDate = useCallback((dateStr) =>
    allLeaves.find(l => {
      const s = toDateStr(l.startDate);
      const e = toDateStr(l.endDate);
      return s && e && dateStr >= s && dateStr <= e;
    }), [allLeaves]);

  const getHoliday = useCallback((dateStr) =>
    holidays.find(h => toDateStr(h.date || h.holidayDate) === dateStr), [holidays]);

  const calendarCells = useMemo(() => {
    const yr = calDate.getFullYear();
    const mo = calDate.getMonth();
    const firstDayIndex = new Date(yr, mo, 1).getDay();
    const daysInMonth = new Date(yr, mo + 1, 0).getDate();
    const daysInPrev = new Date(yr, mo, 0).getDate();
    const cells = [];
    const makeKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dn = daysInPrev - i;
      const d = new Date(yr, mo - 1, dn);
      cells.push({ dateStr: makeKey(d.getFullYear(), d.getMonth(), dn), dayNum: dn, isCurrentMonth: false, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    for (let dn = 1; dn <= daysInMonth; dn++) {
      const d = new Date(yr, mo, dn);
      cells.push({ dateStr: makeKey(yr, mo, dn), dayNum: dn, isCurrentMonth: true, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    const rem = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= rem; i++) {
      const d = new Date(yr, mo + 1, i);
      cells.push({ dateStr: makeKey(d.getFullYear(), d.getMonth(), i), dayNum: i, isCurrentMonth: false, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    return cells;
  }, [calDate]);

  const leavesInMonth = useMemo(() => {
    const yr = calDate.getFullYear();
    const mo = calDate.getMonth();
    const mStart = `${yr}-${String(mo + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(yr, mo + 1, 0).getDate();
    const mEnd = `${yr}-${String(mo + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return allLeaves.filter(l => {
      const s = toDateStr(l.startDate);
      const e = toDateStr(l.endDate);
      return s && e && s <= mEnd && e >= mStart;
    });
  }, [allLeaves, calDate]);

  const fromSource = location.state?.from || (location.pathname.startsWith('/hr') ? 'hr' : location.pathname.startsWith('/admin') ? 'admin' : 'manager');
  const isFromHr = fromSource === 'hr';
  const isFromAdmin = fromSource === 'admin';

  const handleBack = () => {
    if (window.history.length > 1 && location.key !== 'default') {
      navigate(-1);
    } else if (isFromAdmin) {
      navigate('/admin/leaves');
    } else if (isFromHr) {
      navigate('/hr/leaves');
    } else {
      navigate('/manager/leave-approvals');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-500">
        <FiRefreshCw className="animate-spin text-blue-600" size={28} />
        <p className="text-sm font-semibold">Loading employee leave profile...</p>
      </div>
    );
  }

  if (error && !balanceData) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-slate-600 bg-white rounded-2xl border border-slate-200 p-8 max-w-xl mx-auto shadow-sm">
        <FiAlertCircle size={40} className="text-rose-500" />
        <p className="text-base font-bold text-slate-800">{error}</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 text-xs font-bold rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 transition-colors cursor-pointer"
        >
          {isFromAdmin ? 'Back to Leave Administration' : isFromHr ? 'Back to Leave Management' : 'Back to Team Approvals'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto animate-fade-in">

      {/* ─────────────────────────────────────────────────────────────
          1. UNIFIED ELEVATED HEADER CARD (Matching Team Approvals)
      ───────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-sky-600 transition-colors mb-2 cursor-pointer group"
          >
            <FiArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            <span>
              {isFromAdmin ? 'Back to Leave Administration' : isFromHr ? 'Back to Leave Management' : 'Back to Team Approvals'}
            </span>
          </button>

          <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5 flex-wrap">
            <span>Employee Leave Dossier</span>
            <span className="text-slate-300 font-normal">/</span>
            <span className="text-sky-600">{empName}</span>
            <span className="text-xs px-2.5 py-0.5 rounded-full font-mono font-bold bg-sky-50 text-sky-700 border border-sky-200">
              {employeeId}
            </span>
          </h1>

          <p className="text-xs text-slate-500 font-medium mt-1 flex items-center gap-2 flex-wrap">
            {empJobTitle && <span className="text-slate-700 font-semibold">{empJobTitle}</span>}
            {empJobTitle && empDept && <span className="text-slate-300">&bull;</span>}
            {empDept && <span>{empDept}</span>}
            <span className="text-slate-300">&bull;</span>
            <span className="text-slate-500 font-mono">Financial Year {year}</span>
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end flex-wrap">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors shadow-2xs cursor-pointer"
            title="Refresh leave data"
          >
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>

          {!isFromHr && (
            <div className="grid grid-cols-2 sm:flex items-center gap-2 flex-1 sm:flex-initial">
              <button
                type="button"
                onClick={() => setCreditEarnedOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition-colors shadow-2xs cursor-pointer"
              >
                <FiAward size={14} className="text-amber-600" />
                <span>Add Earned</span>
              </button>

              <button
                type="button"
                onClick={() => setAddLeaveOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-xs cursor-pointer"
              >
                <FiPlus size={14} />
                <span>Add Leave</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          2. CONSOLIDATED EXECUTIVE METRICS RIBBON (No Duplication!)
      ───────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Spotlight & Status Bar */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-black text-base shadow-sm shrink-0">
              {initials(empName)}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-slate-900">{empName}</h2>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-white border border-slate-200 text-slate-600 font-mono">
                  {employeeId}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                {empJobTitle || 'Staff Member'} {empDept ? `· ${empDept}` : ''}
              </p>
            </div>
          </div>

          {/* Quick Status Chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {pendingList.length} Pending
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {approvedList.length} Approved
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
              {rejectedList.length} Rejected
            </span>
          </div>
        </div>

        {/* Divided KPI Cells */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
          <div className="p-4 sm:p-5 text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Base Entitlement</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-slate-900">{balanceData?.annualLimit ?? 24}</span>
              <span className="text-xs font-semibold text-slate-500">days</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1 font-medium">Annual Quota</p>
          </div>

          <div className="p-4 sm:p-5 text-center">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1">Earned Leaves</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-blue-600">+{balanceData?.earnedLeave ?? 0}</span>
              <span className="text-xs font-semibold text-blue-600">days</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1 font-medium">{earnedLeaves.length} Duty Credits Logged</p>
          </div>

          <div className="p-4 sm:p-5 text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Paid Taken (YTD)</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-slate-800">{balanceData?.paidTakenYear ?? 0}</span>
              <span className="text-xs font-semibold text-slate-500">days</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1 font-medium">Across FY {year}</p>
          </div>

          <div className="p-4 sm:p-5 text-center bg-sky-50/40">
            <p className="text-[10px] font-bold text-sky-700 uppercase tracking-wider mb-1">Balance Remaining</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl sm:text-3xl font-black text-sky-700">
                {balanceData?.totalPaidLeavesAvailableYear ?? balanceData?.currentAvailableBalance ?? 0}
              </span>
              <span className="text-xs font-semibold text-sky-700">days</span>
            </div>
            <p className="text-[11px] font-bold text-sky-600 mt-1">
              {balanceData?.totalPaidLeavesAvailableMonth ?? 0}d slots left this month
            </p>
          </div>
        </div>

        {/* Loss of Pay (LOP) Alert Ribbon if applicable */}
        {((balanceData?.lopLeavesMonth || 0) > 0 || (balanceData?.lopLeavesYear || 0) > 0) && (
          <div className="bg-rose-50 border-t border-rose-200 px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-rose-800 font-bold">
              <FiTrendingDown size={16} className="text-rose-600" />
              <span>Active Loss of Pay (LOP) Notice:</span>
              <span className="font-medium text-rose-700">
                {balanceData?.lopLeavesMonth ?? 0} days in current month &bull; {balanceData?.lopLeavesYear ?? 0} days in FY {year}
              </span>
            </div>
            <div className="font-mono font-bold text-rose-900 bg-rose-100/90 px-3 py-1 rounded-lg border border-rose-300 shrink-0">
              Est. Salary Deduction: {balanceData?.lopDeductionAmount != null ? `₹${Number(balanceData.lopDeductionAmount).toLocaleString('en-IN')}` : 'Pending Config'}
            </div>
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          3. SLEEK SEGMENTED TABS (Calendar | Applications | Earned)
      ───────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-1 border-b border-slate-200/80">
        <div className="flex items-center p-1 bg-slate-100/90 rounded-xl border border-slate-200/60 shadow-2xs overflow-x-auto max-w-full w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setActiveTab('calendar')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
              activeTab === 'calendar'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            <FiCalendar size={13} />
            <span>Calendar &amp; Schedule</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('applications')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
              activeTab === 'applications'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            <FiFileText size={13} />
            <span>Leave Applications</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
              activeTab === 'applications' ? 'bg-slate-100 text-slate-800' : 'bg-slate-200/70 text-slate-600'
            }`}>
              {allLeaves.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('earned')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
              activeTab === 'earned'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            <FiAward size={13} />
            <span>Earned Leaves Log</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
              activeTab === 'earned' ? 'bg-slate-100 text-slate-800' : 'bg-slate-200/70 text-slate-600'
            }`}>
              {earnedLeaves.length}
            </span>
          </button>
        </div>

        {/* Contextual Sub-toggle / Controls */}
        {activeTab === 'calendar' && (
          <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              onClick={() => setScheduleView('calendar')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                scheduleView === 'calendar' ? 'bg-white text-sky-700 shadow-xs' : 'text-slate-500 hover:text-slate-800 font-medium'
              }`}
            >
              <FiCalendar size={12} /> Calendar
            </button>
            <button
              onClick={() => setScheduleView('list')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                scheduleView === 'list' ? 'bg-white text-sky-700 shadow-xs' : 'text-slate-500 hover:text-slate-800 font-medium'
              }`}
            >
              <FiList size={12} /> List
            </button>
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs overflow-x-auto max-w-full">
            {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map(f => (
              <button
                key={f}
                onClick={() => setAppFilter(f)}
                className={`px-3 py-1 rounded-lg font-bold transition-all shrink-0 cursor-pointer ${
                  appFilter === f ? 'bg-white text-slate-900 shadow-xs font-semibold' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {f === 'ALL' ? `All (${allLeaves.length})` : f === 'PENDING' ? `Pending (${pendingList.length})` : f === 'APPROVED' ? `Approved (${approvedList.length})` : `Rejected (${rejectedList.length})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: CALENDAR & SCHEDULE
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'calendar' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <FiCalendar className="text-sky-600" size={18} />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Leave Calendar &amp; Duty Schedule</h3>
                <p className="text-xs text-slate-500 font-medium">Workforce availability and holidays for {year}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                title="Previous month"
              >
                <FiChevronLeft size={16} />
              </button>
              <span className="font-bold text-slate-900 text-sm min-w-[140px] text-center">
                {calDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() => changeMonth(1)}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                title="Next month"
              >
                <FiChevronRight size={16} />
              </button>
              <button
                onClick={() => setCalDate(new Date())}
                className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-sky-600 border border-sky-200 hover:bg-sky-50 transition-colors ml-1 cursor-pointer"
              >
                Today
              </button>
              {leavesInMonth.length > 0 && (
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1.5 ml-2">
                  <span className="w-2 h-2 rounded-full bg-sky-600" />
                  {leavesInMonth.length} Leave{leavesInMonth.length > 1 ? 's' : ''} in {calDate.toLocaleDateString('en-US', { month: 'short' })}
                </span>
              )}
            </div>
          </div>

          {scheduleView === 'calendar' ? (
            <div className="overflow-x-auto">
              <div className="min-w-[480px] space-y-4">
                <div className="grid grid-cols-7 gap-1.5 text-center text-xs font-bold uppercase tracking-wider text-slate-400 py-1 border-b border-slate-100">
                <span className="text-rose-500">Sun</span>
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span className="text-amber-500">Sat</span>
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {calendarCells.map((cell, idx) => {
                  const { isCurrentMonth, dayNum, isWeekend, dateStr } = cell;
                  const leave = getLeaveForDate(dateStr);
                  const holiday = getHoliday(dateStr);

                  if (!isCurrentMonth) {
                    if (leave && leave.status === 'APPROVED') {
                      return (
                        <div
                          key={`p${idx}`}
                          onClick={() => navigate(`/hr/leave/${leave.id}`)}
                          className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl bg-slate-100/60 border border-slate-200/60 opacity-60 flex flex-col justify-between cursor-pointer hover:opacity-90 transition-all"
                        >
                          <span className="font-bold text-[11px] text-slate-700">{dayNum}</span>
                          <span className="text-[9px] font-bold text-slate-600 truncate">{leave.leaveType}</span>
                        </div>
                      );
                    }
                    return (
                      <div key={`p${idx}`} className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl bg-slate-50/30 border border-transparent flex flex-col justify-between select-none">
                        <span className="font-semibold text-[11px] text-slate-300 opacity-40">{dayNum}</span>
                      </div>
                    );
                  }

                  if (leave && leave.status === 'APPROVED') {
                    const isUnpaid = (leave.unpaidDays || 0) > 0 && (leave.paidDays || 0) === 0;
                    let bg = 'bg-emerald-50/90 border-emerald-300 text-emerald-950';
                    let badge = 'bg-emerald-600 text-white';
                    if (isUnpaid) {
                      bg = 'bg-rose-50/90 border-rose-300 text-rose-950';
                      badge = 'bg-rose-600 text-white';
                    }
                    return (
                      <div
                        key={dateStr}
                        onClick={() => navigate(`/hr/leave/${leave.id}`)}
                        title={`Approved: ${leave.leaveType}`}
                        className={`min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border flex flex-col justify-between transition-all shadow-2xs cursor-pointer ${bg}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-black">{dayNum}</span>
                          <span className="text-[8px] font-black uppercase px-1 rounded bg-white/60">✓</span>
                        </div>
                        <div className="mt-1">
                          <span className={`inline-flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold shadow-2xs truncate ${badge}`}>
                            <FiCheck size={9} /> {leave.leaveType}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  if (leave && leave.status === 'PENDING') {
                    return (
                      <div
                        key={dateStr}
                        onClick={() => navigate(`/hr/leave/${leave.id}`)}
                        title={`Pending: ${leave.leaveType}`}
                        className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-amber-300 bg-amber-50/80 ring-2 ring-amber-500/10 flex flex-col justify-between transition-all cursor-pointer hover:border-amber-400"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-amber-950">{dayNum}</span>
                          <span className="text-[8px] font-bold uppercase px-1 rounded bg-amber-100 text-amber-800">Pend</span>
                        </div>
                        <div className="mt-1">
                          <span className="inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-600 text-white truncate">
                            <FiClock size={9} /> {leave.leaveType}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  if (leave) {
                    return (
                      <div
                        key={dateStr}
                        onClick={() => navigate(`/hr/leave/${leave.id}`)}
                        className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-rose-300 bg-rose-50/90 flex flex-col justify-between cursor-pointer hover:border-rose-400 transition-all"
                      >
                        <span className="text-sm font-bold text-rose-950">{dayNum}</span>
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-rose-600 text-white truncate">
                          <FiX size={9} /> {leave.leaveType}
                        </span>
                      </div>
                    );
                  }

                  if (holiday) {
                    return (
                      <div
                        key={dateStr}
                        title={`Holiday: ${holiday.name}`}
                        className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-teal-200 bg-teal-50/70 text-teal-900 flex flex-col justify-between"
                      >
                        <span className="text-xs font-bold text-teal-800">{dayNum}</span>
                        <span className="text-[9px] font-bold text-teal-700 truncate">🎉 {holiday.name || 'Holiday'}</span>
                      </div>
                    );
                  }

                  if (isWeekend) {
                    return (
                      <div
                        key={dateStr}
                        className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-slate-200/50 bg-slate-50/70 text-slate-400 flex flex-col justify-between"
                      >
                        <span className="text-xs font-bold text-slate-400">{dayNum}</span>
                        <span className="text-[9px] font-semibold text-slate-400">Off</span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={dateStr}
                      className="min-h-[72px] sm:min-h-[82px] p-2 rounded-xl border border-slate-200/80 bg-white text-slate-700 flex flex-col justify-between hover:bg-slate-50/50 transition-colors"
                    >
                      <span className="text-xs font-bold text-slate-700">{dayNum}</span>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-x-5 gap-y-2 pt-3 border-t border-slate-100 flex-wrap text-xs font-medium text-slate-600">
                <span className="text-slate-400 text-[11px] uppercase tracking-wider font-bold mr-1">Legend:</span>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="text-slate-700 font-semibold">Paid Leave</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0" />
                  <span className="text-slate-700 font-semibold">LOP / Unpaid Leave</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                  <span className="text-slate-700 font-semibold">Pending Approval</span>
                </div>
                <div className="flex items-center gap-2 text-teal-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-teal-500 shrink-0" />
                  <span>Public Holiday</span>
                </div>
              </div>
            </div>
            </div>
          ) : (
            /* List View */
            <div className="space-y-2">
              {allLeaves.length === 0 ? (
                <p className="text-xs text-slate-400 py-8 text-center">No leave records for this employee.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {allLeaves.map(l => {
                    const isApp = l.status === 'APPROVED';
                    const isPend = l.status === 'PENDING';
                    const rowBg = isApp ? 'bg-sky-50/30 hover:bg-sky-50/60' : isPend ? 'bg-amber-50/30 hover:bg-amber-50/60' : 'bg-slate-50/30 hover:bg-slate-50/60';
                    const dot = isApp ? 'bg-emerald-500' : isPend ? 'bg-amber-500' : 'bg-rose-500';
                    return (
                      <div
                        key={l.id}
                        onClick={() => navigate(`/hr/leave/${l.id}`)}
                        className={`p-4 flex items-center justify-between ${rowBg} cursor-pointer transition-colors`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-2.5 h-2.5 rounded-full ${dot} shrink-0`} />
                          <div>
                            <p className="text-xs font-bold text-slate-800">{l.leaveType}</p>
                            <p className="text-xs text-slate-500">{fmtDate(l.startDate)} &rarr; {fmtDate(l.endDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-slate-700">{l.daysCount}d</span>
                          <span className="text-[11px] text-slate-400 font-mono">
                            {l.paidDays ?? 0}P / <span className={(l.unpaidDays || 0) > 0 ? "text-rose-600 font-bold" : ""}>{l.unpaidDays ?? 0}U</span>
                          </span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            isApp ? 'bg-emerald-100 text-emerald-800' : isPend ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                          }`}>
                            {l.status}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: LEAVE APPLICATIONS FEED
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'applications' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Applications History</h3>
              <p className="text-xs text-slate-500 font-medium">All leave requests filed by or on behalf of this employee</p>
            </div>
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
              {filteredApplications.length} records
            </span>
          </div>

          {filteredApplications.length === 0 ? (
            <div className="py-12 text-center text-slate-400 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
              <FiFileText className="mx-auto mb-2 text-slate-300" size={28} />
              <p className="text-xs font-bold text-slate-700">No applications match the current filter</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Switch filter to 'All' or file a new application above.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {filteredApplications.map(l => {
                const isApp = l.status === 'APPROVED';
                const isPend = l.status === 'PENDING';
                const statusColor = isApp
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : isPend
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : 'bg-rose-50 text-rose-700 border-rose-200';

                return (
                  <div
                    key={l.id}
                    onClick={() => navigate(`/hr/leave/${l.id}`)}
                    className="p-4 hover:bg-slate-50/80 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-xs text-slate-900">{l.leaveType}</span>
                        {l.isHalfDay && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">
                            Half Day ({l.halfDaySession || 'First Half'})
                          </span>
                        )}
                        {l.addedByManager && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                            Added by Manager
                          </span>
                        )}
                        {(l.unpaidDays || 0) > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200">
                            LOP Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-medium">
                        {fmtDate(l.startDate)} &rarr; {fmtDate(l.endDate)} &bull; {l.daysCount} total day{l.daysCount > 1 ? 's' : ''}
                      </p>
                      {l.reason && (
                        <p className="text-xs text-slate-600 line-clamp-1 italic">
                          "{l.reason}"
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-auto shrink-0">
                      <div className="text-right text-xs font-mono">
                        <p className="font-bold text-slate-900">{l.daysCount} days</p>
                        <p className="text-[10px] text-slate-400">
                          {l.paidDays ?? 0}P / <span className={(l.unpaidDays || 0) > 0 ? "text-rose-600 font-bold" : ""}>{l.unpaidDays ?? 0}U</span>
                        </p>
                      </div>

                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${statusColor}`}>
                        {l.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: EARNED LEAVES LOG
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'earned' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <FiAward className="text-amber-600" size={20} />
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Earned Leaves Credit History</h3>
                <p className="text-xs text-slate-500 font-medium">Approved compensation days for weekend, holiday, or emergency duties</p>
              </div>
            </div>

            {!isFromHr && (
              <button
                type="button"
                onClick={() => setCreditEarnedOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-xs cursor-pointer"
              >
                <FiPlus size={13} />
                <span>Credit Duty Day</span>
              </button>
            )}
          </div>

          {earnedLeaves.length === 0 ? (
            <div className="py-12 text-center text-slate-400 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
              <FiAward className="mx-auto mb-2 text-slate-300" size={32} />
              <p className="text-xs font-bold text-slate-700">No Earned Leave Credits Logged</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Use "Credit Duty Day" above to record compensation for weekend duties.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {earnedLeaves.map((el) => (
                <div
                  key={el.id}
                  className="p-4 hover:bg-slate-50/60 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                        <FiCalendar size={13} className="text-slate-400" />
                        Duty Date: {fmtDate(el.effectiveDate)}
                      </span>
                      <span className="font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs">
                        +{el.creditDays} day{el.creditDays > 1 ? 's' : ''} credit
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      <strong className="text-slate-700">Duty Description:</strong> {el.remarks || 'Weekend or public holiday coverage completed.'}
                    </p>
                  </div>

                  <div className="text-left sm:text-right text-xs text-slate-400 shrink-0">
                    <p>
                      Authorized by: <strong className="text-slate-700 font-semibold">{el.createdBy || 'Manager'}</strong>
                    </p>
                    {el.createdAt && (
                      <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                        Logged: {fmtDate(el.createdAt)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Leave for this Subordinate Modal */}
      <ManagerAddLeaveModal
        isOpen={addLeaveOpen}
        onClose={() => setAddLeaveOpen(false)}
        subordinates={[{
          employeeId: employeeId,
          fullName: empName,
          jobTitle: empJobTitle,
          department: empDept,
        }]}
        preSelectedEmp={employeeId}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Credit Earned Leave for this Subordinate Modal */}
      <CreditEarnedLeaveModal
        isOpen={creditEarnedOpen}
        onClose={() => setCreditEarnedOpen(false)}
        subordinates={[{
          employeeId: employeeId,
          fullName: empName,
          jobTitle: empJobTitle,
          department: empDept,
        }]}
        preSelectedEmp={employeeId}
        onSuccess={() => {
          fetchData();
        }}
      />
    </div>
  );
}
